"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { ArrowRight } from "lucide-react";
import { DATA_REFRESHED_EVENT } from "@/lib/data-refresh";
import { EmptyState } from "./module-panels";
import { FreshnessTag, Panel } from "./ui";
import type { LeverageRegimeData, LeverageRegimeName, LeverageRegimePoint } from "@/lib/types";

type Lookback = "3d" | "7d" | "30d";

const LOOKBACKS: Array<{ value: Lookback; label: string }> = [
  { value: "3d", label: "Short · 3D" },
  { value: "7d", label: "Mid · 7D" },
  { value: "30d", label: "Long · 30D" }
];

const REGIMES: Record<LeverageRegimeName, { label: string; short: string; color: string; surface: string; description: string }> = {
  leveraged_rally: {
    label: "Leveraged rally",
    short: "Price ↑ · OI ↑",
    color: "#fbbf24",
    surface: "rgba(251, 191, 36, 0.08)",
    description: "Price and outstanding positioning are expanding together. Direction and durability still need confirmation."
  },
  leverage_into_weakness: {
    label: "Leverage into weakness",
    short: "Price ↓ · OI ↑",
    color: "#fb7185",
    surface: "rgba(251, 113, 133, 0.07)",
    description: "New positioning is entering while price weakens. OI alone cannot prove those positions are directional shorts."
  },
  deleveraging_selloff: {
    label: "Deleveraging sell-off",
    short: "Price ↓ · OI ↓",
    color: "#60a5fa",
    surface: "rgba(96, 165, 250, 0.07)",
    description: "Price and open interest are contracting together. Closures may be voluntary or forced."
  },
  deleveraging_rally: {
    label: "Deleveraging rally",
    short: "Price ↑ · OI ↓",
    color: "#34d399",
    surface: "rgba(52, 211, 153, 0.07)",
    description: "Price is rising while positions close. This is a spot-led candidate, not proof of spot demand."
  }
};

function signedPercent(value: number) {
  return `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;
}

function trajectoryWindowLabel(pointCount: number) {
  const hours = pointCount * 4;
  return hours % 24 === 0 ? `${hours / 24} days` : `${hours} hours`;
}

function RegimeTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: LeverageRegimePoint }> }) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  const regime = REGIMES[point.regime];
  return (
    <div className="rounded-md border border-[var(--hc-border-strong)] bg-[var(--hc-surface-1)] px-3 py-2 text-[11px] shadow-xl">
      <p className="font-semibold" style={{ color: regime.color }}>{regime.label}</p>
      <p className="mt-1 text-[var(--hc-muted)]">Price {signedPercent(point.price_change_pct)} · OI {signedPercent(point.oi_change_pct)}</p>
      <p className="mt-1 text-[var(--hc-muted)]">{new Date(point.time).toLocaleString("en-GB", { timeZone: "UTC", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} UTC</p>
    </div>
  );
}

type TrajectoryArrowProps = {
  cx?: number;
  cy?: number;
  index?: number;
  payload?: LeverageRegimePoint;
  trajectory: LeverageRegimePoint[];
  extent: { x: number; y: number };
};

function TrajectoryArrow({ cx, cy, index, payload, trajectory, extent }: TrajectoryArrowProps) {
  if (cx === undefined || cy === undefined || index === undefined || !payload) return null;
  const destination = trajectory[index + 1];
  const previous = trajectory[index - 1];
  const normalizedX = destination
    ? (destination.oi_change_pct - payload.oi_change_pct) / extent.x
    : previous
      ? (payload.oi_change_pct - previous.oi_change_pct) / extent.x
      : 1;
  const normalizedY = destination
    ? (destination.price_change_pct - payload.price_change_pct) / extent.y
    : previous
      ? (payload.price_change_pct - previous.price_change_pct) / extent.y
      : 0;
  const angle = Math.atan2(-normalizedY, normalizedX) * 180 / Math.PI;
  const newest = index === trajectory.length - 1;
  const size = newest ? 19 : 15;
  return (
    <ArrowRight
      x={cx - size / 2}
      y={cy - size / 2}
      width={size}
      height={size}
      stroke={REGIMES[payload.regime].color}
      strokeWidth={newest ? 2.7 : 2.2}
      opacity={newest ? 1 : 0.72}
      transform={`rotate(${angle} ${cx} ${cy})`}
      style={{ pointerEvents: "none" }}
      aria-hidden="true"
    />
  );
}

export function LeverageRegimeWidget() {
  const [data, setData] = useState<LeverageRegimeData | null>(null);
  const [lookback, setLookback] = useState<Lookback>("7d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (foreground = false) => {
    if (foreground) setLoading(true);
    try {
      const response = await fetch("/api/coinglass/leverage-regime?limit=900", { cache: "no-store", credentials: "include" });
      if (!response.ok) throw new Error("The CoinGlass leverage regime is temporarily unavailable.");
      const payload = await response.json() as LeverageRegimeData;
      if (!payload.lookbacks?.["7d"]?.trajectory?.length) throw new Error("CoinGlass returned no aligned price and OI history.");
      setData(payload);
      setError(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The CoinGlass leverage regime is temporarily unavailable.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(true);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, 60_000);
    const reload = () => void load();
    window.addEventListener(DATA_REFRESHED_EVENT, reload);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(DATA_REFRESHED_EVENT, reload);
    };
  }, [load]);

  const selected = data?.lookbacks[lookback];
  const current = selected?.current;
  const regime = current ? REGIMES[current.regime] : null;
  const extent = useMemo(() => {
    if (!selected) return { x: 5, y: 5 };
    const x = Math.max(selected.thresholds.oi_pct * 2.4, ...selected.trajectory.map((point) => Math.abs(point.oi_change_pct))) * 1.18;
    const y = Math.max(selected.thresholds.price_pct * 2.4, ...selected.trajectory.map((point) => Math.abs(point.price_change_pct))) * 1.18;
    return { x: Math.max(x, 1), y: Math.max(y, 1) };
  }, [selected]);

  return (
    <section id="leverage-regime" aria-labelledby="leverage-regime-heading">
      <Panel className="overflow-hidden px-5 py-6 md:px-7 md:py-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--hc-muted)]">Positioning regime</p>
            <h2 id="leverage-regime-heading" className="mt-2 text-2xl font-semibold tracking-[-0.035em] text-[var(--hc-text)]">BTC Leverage Regime</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-[var(--hc-muted)]">Price change versus coin-denominated open-interest change. Four quadrants describe how price and leverage are moving together; they are not directional trade signals.</p>
          </div>
          {data ? <FreshnessTag status={data.cache_status ?? "live"} source="CoinGlass" collectedAt={data.cache_collected_at ?? data.collected_at} /> : null}
        </div>

        {data?.cache_status === "stale" ? <div role="status" className="mt-4 rounded-md border border-amber-400/40 bg-amber-400/5 px-4 py-3 text-sm leading-6 text-amber-200">This regime is the last validated CoinGlass snapshot and is not current. Price and open-interest changes below should not be treated as live.</div> : null}

        <div className="mt-5 grid w-full grid-cols-3 gap-1 rounded-lg border border-[var(--hc-border)] bg-[var(--hc-surface-2)] p-1 sm:w-fit" role="group" aria-label="Leverage regime lookback">
          {LOOKBACKS.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setLookback(item.value)}
              aria-pressed={lookback === item.value}
              className={`min-h-11 rounded-md px-3 text-[11px] font-semibold transition-colors sm:min-h-9 ${lookback === item.value ? "bg-[var(--hc-blue-soft)] text-[var(--hc-text)]" : "text-[var(--hc-muted)] hover:text-[var(--hc-text)]"}`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {current && selected && regime ? (
          <>
            <div className="mt-5 grid gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-md border border-[var(--hc-border)] bg-[var(--hc-surface-2)] px-4 py-3.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--hc-muted)]">Current regime</p>
                <p className="mt-1.5 text-sm font-semibold" style={{ color: regime.color }}>{regime.label}</p>
              </div>
              <div className="rounded-md border border-[var(--hc-border)] bg-[var(--hc-surface-2)] px-4 py-3.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--hc-muted)]">BTC change</p>
                <p className={`mt-1.5 text-lg font-semibold ${current.price_change_pct >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{signedPercent(current.price_change_pct)}</p>
              </div>
              <div className="rounded-md border border-[var(--hc-border)] bg-[var(--hc-surface-2)] px-4 py-3.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--hc-muted)]">OI change</p>
                <p className={`mt-1.5 text-lg font-semibold ${current.oi_change_pct >= 0 ? "text-amber-300" : "text-sky-300"}`}>{signedPercent(current.oi_change_pct)}</p>
              </div>
              <div className="rounded-md border border-[var(--hc-border)] bg-[var(--hc-surface-2)] px-4 py-3.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--hc-muted)]">Signal state</p>
                <p className="mt-1.5 text-sm font-semibold capitalize text-[var(--hc-text)]">{current.conviction}</p>
                <p className="mt-1 text-[10px] text-[var(--hc-muted)]">{selected.regime_bars ? `${selected.regime_bars * 4}h confirmed` : "Inside adaptive dead zone"}</p>
              </div>
            </div>

            <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
              <div className="rounded-lg border border-[var(--hc-border)] bg-[var(--hc-surface-2)] p-3 md:p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-xs font-semibold text-[var(--hc-text)]">Coin-denominated open interest change × BTC price change</p>
                    <p className="mt-1 text-[10px] text-[var(--hc-muted)]">Last {selected.trajectory.length} closed 4h observations ({trajectoryWindowLabel(selected.trajectory.length)}) · arrows run oldest → newest</p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] text-[var(--hc-muted)]"><ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /> trajectory · OI → · Price ↑</span>
                </div>
                <div className="h-[360px] w-full md:h-[460px]" aria-label="Price and open-interest trajectory with arrows pointing from older to newer observations">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={selected.trajectory} margin={{ top: 12, right: 14, bottom: 16, left: 4 }}>
                      <ReferenceArea x1={0} x2={extent.x} y1={0} y2={extent.y} fill={REGIMES.leveraged_rally.surface} strokeOpacity={0} />
                      <ReferenceArea x1={0} x2={extent.x} y1={-extent.y} y2={0} fill={REGIMES.leverage_into_weakness.surface} strokeOpacity={0} />
                      <ReferenceArea x1={-extent.x} x2={0} y1={-extent.y} y2={0} fill={REGIMES.deleveraging_selloff.surface} strokeOpacity={0} />
                      <ReferenceArea x1={-extent.x} x2={0} y1={0} y2={extent.y} fill={REGIMES.deleveraging_rally.surface} strokeOpacity={0} />
                      <ReferenceArea x1={-selected.thresholds.oi_pct} x2={selected.thresholds.oi_pct} y1={-selected.thresholds.price_pct} y2={selected.thresholds.price_pct} fill="rgba(148,163,184,0.11)" stroke="rgba(148,163,184,0.32)" strokeDasharray="4 4" />
                      <CartesianGrid stroke="rgba(148,163,184,0.10)" strokeDasharray="3 5" />
                      <ReferenceLine x={0} stroke="rgba(226,232,240,0.5)" />
                      <ReferenceLine y={0} stroke="rgba(226,232,240,0.5)" />
                      <XAxis type="number" dataKey="oi_change_pct" domain={[-extent.x, extent.x]} tickFormatter={(value) => `${value.toFixed(0)}%`} tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} name="Coin OI change" />
                      <YAxis type="number" dataKey="price_change_pct" domain={[-extent.y, extent.y]} tickFormatter={(value) => `${value.toFixed(0)}%`} tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} width={42} name="Price change" />
                      <Tooltip cursor={{ stroke: "rgba(148,163,184,0.28)", strokeDasharray: "3 3" }} content={<RegimeTooltip />} />
                      <Line
                        type="linear"
                        dataKey="price_change_pct"
                        stroke="rgba(148,163,184,0.42)"
                        strokeWidth={1.4}
                        isAnimationActive={false}
                        activeDot={false}
                        dot={(props) => <TrajectoryArrow {...props} trajectory={selected.trajectory} extent={extent} />}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <aside className="space-y-3 rounded-lg border border-[var(--hc-border)] bg-[var(--hc-surface-2)] p-4">
                <div className="rounded-md border px-4 py-3" style={{ borderColor: `${regime.color}55`, background: regime.surface }}>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: regime.color }}>{regime.short}</p>
                  <p className="mt-2 text-base font-semibold text-[var(--hc-text)]">{regime.label}</p>
                  <p className="mt-2 text-xs leading-5 text-[var(--hc-muted)]">{regime.description}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--hc-muted)]">Adaptive dead zone</p>
                  <p className="mt-2 text-xs leading-5 text-[var(--hc-muted)]">Low conviction inside ±{selected.thresholds.price_pct.toFixed(2)}% price or ±{selected.thresholds.oi_pct.toFixed(2)}% OI. Thresholds use the trailing magnitude distribution.</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(REGIMES) as LeverageRegimeName[]).map((name) => (
                    <div key={name} className="rounded-md border border-[var(--hc-border)] px-2.5 py-2">
                      <span className="block h-1.5 w-5 rounded-full" style={{ background: REGIMES[name].color }} />
                      <p className="mt-1.5 text-[10px] leading-4 text-[var(--hc-muted)]">{REGIMES[name].short}</p>
                    </div>
                  ))}
                </div>
              </aside>
            </div>

            <div className="mt-4">
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--hc-muted)]">Last 30 regime observations</p>
                <p className="text-[10px] text-[var(--hc-muted)]">4h cells</p>
              </div>
              <div className="grid grid-cols-[repeat(30,minmax(4px,1fr))] gap-1" aria-label="Recent leverage regimes">
                {selected.recent_regimes.map((point) => (
                  <span key={point.time} title={`${REGIMES[point.regime].label} · ${point.conviction}`} className="h-3 rounded-sm" style={{ background: REGIMES[point.regime].color, opacity: point.conviction === "transition" ? 0.28 : point.conviction === "extreme" ? 1 : 0.72 }} />
                ))}
              </div>
            </div>
            <p className="mt-4 text-[10px] leading-4 text-[var(--hc-muted)]">CoinGlass · Binance BTCUSDT · 4h · last closed candle. OI is coin-denominated to avoid price mechanically inflating the classification.</p>
          </>
        ) : loading ? (
          <div className="mt-5 h-[650px] animate-pulse rounded-lg bg-[var(--hc-surface-2)]" />
        ) : (
          <div className="mt-5"><EmptyState>CoinGlass price and open-interest history will appear after a validated response.</EmptyState></div>
        )}

        {error ? <p role="alert" className="mt-4 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">{error}</p> : null}
      </Panel>
    </section>
  );
}
